import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSlug from "rehype-slug"; // Automatically generate IDs for headings
import rehypeAutolinkHeadings from "rehype-autolink-headings";
import "github-markdown-css/github-markdown.css";
import "../styles/docs.css";
import { IoSearchOutline } from "react-icons/io5";
import DotLineMenu from "../components/DotLineMenu";
import VideoWithLoader from "../components/VideoWithLoader";
import { CodeBlockWithCopy } from "../components/CodeBlockWithCopy";
import PageNavigation from "../components/PageNavigation";
import navigationMap from "../utils/navigationMap";
import WelcomeComponent from "../components/WelcomeComponent";
import IntegrationsWelcomeComponent from "../components/IntegrationsWelcomeComponent";
import GettingStartedWelcomeComponent from "../components/GettingStartedWelcomeComponent";
import allNavigationsMap from "../utils/allNavigationsMap";

import NavBar from "../components/NavBar";
import SkeletonLoaderHeadings from "../components/SkeletonLoaderHeadings";
import SkeletonLoader from "../components/SkeletonLoader";

const Docs = () => {
  const [markdown, setMarkdown] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [docsItem, setDocsItem] = useState("integration-overview");
  const [activeHeading, setActiveHeading] = useState("");
  const markdownRef = useRef(null);
  const [headings, setHeadings] = useState([]);
  const [filteredHeadings, setFilteredHeadings] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    //console.log(markdown);
    //window.screenTo(0, 0);
    if (markdownRef.current) {
      // Query headings in the rendered markdown using the ref
      const headingElements = markdownRef.current.querySelectorAll(
        "h1, h2, h3, h4, h5, h6"
      );

      const headingsList = Array.from(headingElements).map((heading, index) => {
        if (!heading.id) {
          heading.id = `${heading.tagName.toLowerCase()}-${index}`;
        }
        return {
          text: heading.innerText,
          id: heading.id,
          element: heading,
        };
      });

      if (markdownRef.current) {
        markdownRef.current.scrollIntoView({
          behavior: "smooth",
          top: 0,
        });
      }

      // if (headingsList[0]?.id) {
      //   //console.log(headingsList[0].id);
      //   scrollToHeading(headingsList[0].id);
      // }
      setHeadings(headingsList);
    }
  }, [markdown]);

  const searchRef = useRef(null);

  const handleClickOutside = (event) => {
    //console.log("IN ref");
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeading(entry.target.id);
            //console.log("Intersecting id:", activeHeading);
          }
        });
      },
      {
        rootMargin: "-18% 0px -52% 0px", // This ensures the heading is in the middle of the screen
      }
    );

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    // Cleanup function to unobserve all
    return () => {
      observer.disconnect();
    };
  }, [headings]);

  const searchDocs = (query) => {
    ///console.log("Here");
    const lowerCaseTerm = query?.toLowerCase();
    const results = [];
    //console.log("Here");
    //console.log(lowerCaseTerm);
    Object.keys(allNavigationsMap).forEach((docType) => {
      const section = allNavigationsMap[docType];
      //console.log(section);
      // Check if the parent name matches the search term
      if (section.name.toLowerCase().includes(lowerCaseTerm)) {
        results.push({
          type: "parent",
          name: section.name,
          link: section.link,
        });
      }

      // // Check the individual items inside each section
      if (section.items) {
        section.items.forEach((item) => {
          const key = Object.keys(item)[0];
          const currItem = item[key];

          if (currItem.name.toLowerCase().includes(lowerCaseTerm)) {
            results.push({
              type: "item",
              name: currItem.name,
              link: currItem.link,
              section: section.name,
            });
          }
        });
      }
    });

    // Update the state with the search results
    setFilteredDocs(results);
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query); // Update input value

    // Search docs and headings based on the current input value
    searchDocs(query);
    const filteredHeadings = headings.filter((heading) =>
      heading.text.toLowerCase().includes(query.toLowerCase())
    );

    // Set the filtered results directly in state or handle them as needed
    setFilteredHeadings(filteredHeadings); // Update filteredHeadings state
    //console.log(filteredDocs);
    //console.log(filteredHeadings);
  };

  const scrollToHeading = (id) => {
    //console.log("id " + id);
    const headingElement = document.getElementById(id);
    if (headingElement) {
      headingElement.scrollIntoView({ behavior: "smooth" });
      //setSearchQuery("");
      setActiveHeading(id);
    }
  };

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const location = useLocation();
  const [docType, setDocType] = useState("");

  useEffect(() => {
    //setLoading(true);
    const pathSegments = location.pathname.split("/");
    ///console.log(pathSegments);
    const docsItem = pathSegments[pathSegments.length - 1];
    setDocsItem(docsItem);
    //console.log(pathSegments);
    const docType =
      pathSegments[1] === "apis"
        ? "apis"
        : pathSegments[pathSegments.length - 2];
    setDocType(docType);
    //console.log(location);
    setLoading(true); // Set loading to true when fetching starts
    if (docsItem) {
      import(`../content/${docType}/${docsItem}.md`)
        .then((res) => fetch(res.default))
        .then((response) => response.text())
        .then((text) => {
          setMarkdown(text);
          setLoading(false); // Set loading to false when fetching completes
        })
        .catch(() => {
          setMarkdown("# Content not found");
          setLoading(false); // Stop loading on error
        });
    } else {
      setMarkdown("# No document specified");
      setLoading(false); // Stop loading if no document is specified
    }
    //setLoading(false);
  }, [location.pathname, docsItem]);

  return (
    <>
      <div className="flex flex-col w-screen !overflow-hidden h-full">
        <div className="flex justify-between items-center sticky top-0 h-[12vh] mx-12">
          <Link to="/">
            {" "}
            <img
              width="170"
              height="55"
              src="/assets/images/new_logo_full.svg"
            />
          </Link>
          <div
            style={{ position: "relative", width: "100%", maxWidth: "307px" }}
          >
            <div style={{ position: "relative" }} ref={searchRef}>
              <input
                type="text"
                className="form-control !h-[33px] !mt-0 !placeholder-[#676060]"
                placeholder="What are you looking for?"
                value={searchQuery}
                onChange={handleInputChange}
                onClick={() => setSearchOpen(true)}
                // onBlur={() => setSearchQuery('')}
                style={{
                  width: "100%",
                  height: "44px",
                  padding: "8px 12px 8px 40px",
                  fontSize: "12px",
                  border: "1px solid #EBEBEB",
                  borderRadius: "6px",
                  background: "#F4F8FB",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  color: "#676060",
                  top: "50%",
                  left: "8px",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              >
                <IoSearchOutline />
              </span>
              {searchOpen && searchQuery && (
                <ul
                  onClick={() => setSearchOpen(false)}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: "0",
                    right: "0",
                    background: "#ffffff",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    zIndex: 1,
                    maxHeight: "200px",
                    overflowY: "auto",
                    width: "100%",
                    padding: "0",
                    margin: "4px 0 0 0",
                    listStyle: "none",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  {filteredHeadings.length > 0 &&
                    filteredHeadings.map((heading) => (
                      <li
                        key={heading.id}
                        onClick={() => {
                          scrollToHeading(heading.id);
                          setSearchQuery("");
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                      >
                        {heading.text}{" "}
                        <span>
                          -{" "}
                          <span className="text-[#27AE60] font-medium">
                            On this page
                          </span>
                        </span>
                      </li>
                    ))}
                  {filteredDocs?.length > 0 &&
                    filteredDocs.map((item) => (
                      <li
                        key={item.id}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          navigate(item.link);
                          setSearchQuery("");
                        }}
                      >
                        {item.name}{" "}
                        {item.section && (
                          <span>
                            -{" "}
                            <span className="text-[#27AE60] font-medium">
                              {item.section}
                            </span>
                          </span>
                        )}
                      </li>
                    ))}
                  {filteredDocs?.length === 0 &&
                    filteredHeadings.length === 0 && (
                      <li style={{ padding: "10px 15px", textAlign: "center" }}>
                        No results found
                      </li>
                    )}
                </ul>
              )}
            </div>
          </div>
          <NavBar />
        </div>
        <div
          className="flex justify-start sticky top-0 items-center h-[6vh] px-12 gap-2 border-t border-b border-[#E4E4E4]"
          style={{ boxShadow: "0px 25px 21px rgba(0, 0, 0, 0.04)" }}
        >
          {" "}
          <p className="font-railway text-[#969696] text-[10px]">
            {" "}
            Documentation{" "}
          </p>{" "}
          <svg
            width="5"
            height="6"
            viewBox="0 0 5 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L3.625 3.25L1 5.5"
              stroke="#3DB766"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <p
            className="font-railway text-[#969696] text-[10px] hover:cursor-pointer"
            onClick={() => navigate(allNavigationsMap[docType]?.link)}
          >
            {" "}
            {allNavigationsMap[docType]?.name}{" "}
          </p>{" "}
          <svg
            width="5"
            height="6"
            viewBox="0 0 5 6"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L3.625 3.25L1 5.5"
              stroke="#3DB766"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>{" "}
          <p className="font-railway text-[#27AE60] text-[10px]">
            {" "}
            {navigationMap[docsItem]?.curr}{" "}
          </p>
        </div>
        <div className="flex bg-white h-[81vh] overflow-y-auto overflow-x-hidden mb-1">
          <div
            className="w-[20vw] p-4 border-r sticky top-0 border-[#E4E4E4]"
            style={{ boxShadow: "0px 25px 21px rgba(0, 0, 0, 0.04)" }}
          >
            <DotLineMenu />
          </div>
          {loading ? (
            <SkeletonLoader />
          ) : (
            <div
              className="w-[62vw] p-8"
              style={{ paddingBottom: "3rem" }}
              ref={markdownRef}
            >
              {docsItem === "api-overview" ? <WelcomeComponent /> : null}
              {docsItem === "integration-overview" ? (
                <IntegrationsWelcomeComponent />
              ) : null}
              {docsItem === "introduction" ? (
                <GettingStartedWelcomeComponent />
              ) : null}
              <ReactMarkdown
                className="markdown text-[#262A36]"
                children={markdown}
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, rehypeSlug, rehypeAutolinkHeadings]}
                components={{
                  img: ({ node, ...props }) => (
                    <span style={{ display: "flex", justifyContent: "center" }}>
                      <img
                        {...props}
                        style={{
                          width: "auto",
                          maxWidth: "100%",
                          height: "auto",
                          margin: "20px 0",
                          border: "2px solid #ccc",
                          borderRadius: "8px",
                          padding: "5px",
                        }}
                        alt={props.alt}
                      />
                    </span>
                  ),
                  video: VideoWithLoader,
                  h1: ({ node, ...props }) => (
                    <h1
                      {...props}
                      style={{ fontFamily: "Raleway", lineHeight: "1.4" }}
                    />
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      {...props}
                      style={{ fontFamily: "Roboto", lineHeight: "1.6" }}
                    />
                  ),
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || "");
                    return !inline ? (
                      <CodeBlockWithCopy language={match ? match[1] : ""}>
                        {children}
                      </CodeBlockWithCopy>
                    ) : (
                      <code
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: "2px 4px",
                          borderRadius: "4px",
                          fontFamily: "monospace",
                        }}
                      >
                        {children}
                      </code>
                    );
                  },
                }}
              />
              <PageNavigation
                prevLink={
                  navigationMap[docsItem]?.prevLink
                    ? navigationMap[docsItem].prevLink
                    : ""
                }
                prevText={
                  navigationMap[docsItem]?.prevText
                    ? navigationMap[docsItem].prevText
                    : ""
                }
                nextLink={
                  navigationMap[docsItem]?.nextLink
                    ? navigationMap[docsItem].nextLink
                    : ""
                }
                nextText={
                  navigationMap[docsItem]?.nextText
                    ? navigationMap[docsItem].nextText
                    : ""
                }
              />
            </div>
          )}
          <div className="w-[18vw] p-4 flex flex-col sticky top-0 py-[8px] border-l border-[#E4E4E4]">
            <p className="font-raleway text-[12px] text-[#898989]">
              {" "}
              On this page
            </p>
            {loading ? (
              <SkeletonLoaderHeadings />
            ) : (
              headings.map((heading) => (
                <div
                  className={`font-poppins font-normal cursor-pointer text-[13px] flex !flex-row items-center gap-4 justify-start text-[#262A36] ${
                    activeHeading === heading.id
                      ? "!text-[#27AE60] !bg-[#e4fef4] !mx-[-1.3rem]"
                      : ""
                  }`}
                  key={heading.id}
                  onClick={() => scrollToHeading(heading.id)}
                >
                  {activeHeading === heading.id && (
                    <span className="w-0.5 h-auto px-0.5 py-3 bg-[#27AE60]" />
                  )}
                  <span
                    style={{
                      padding: "8px 10px",
                    }}
                  >
                    {heading.text}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Docs;
